import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import Config from '@skin_config';
import NumericInput from '@common/components/numericInput';

import StaticStake from "../../../../components/staticStake";
import MoneyAmount from "@common/components/moneyAmount";
import Helpers from "../../../../../../../BettingTerminal.Common/helpers/helperFunctions";
import { PrintBetBuilderTicket } from "../../../../../actions/terminal";
import { betBuilderPlaceBet, calculateBetBuilderPossibleWin } from "./helpers";
import { GetToken, GetUser, GetUserId } from "../../../../../helpers/selectors";
import { GetCurrencyConfig } from "@common/helpers/selectors";
import { BetCalculationsEnum, getBetCalculations } from "@betslip/calculation";
import { swarmRequest } from "@common/helpers/util/requestMiddleware";
import { PopupContext } from "@app/dev/contexts/popup";
import { useTranslation } from "@common/hooks/translation";
import { useBalance } from "@app/dev/terminal/hooks/balance";

import './style.scss';
import { applyUser, createUserBet, logoutUser, verifyUserAmount } from '@app/dev/helpers/verification';
import VerificationAccount from './verificationAccount';
import { POPUPS } from '@common/constants/popups';
import VerificationToggle from './verificationToggle';
import ToastWrapper from '@common/helpers/toastWrapper';


const BetBuliderBetslip = ({ betData, onClear, user, currencyConfig, token, partner, dispatch }) => {
  const t = useTranslation();
  const terminalBalance = useBalance();
  const [stake, setStake] = useState();
  const [{ closePopupByType }] = useContext(PopupContext);
  const [placeBetError, setPlaceBetError] = useState('');
  const [balanceError, setBalanceError] = useState('')
  const [placeBetLoading, setPlaceBetLoading] = useState(false);
  const [verificationDisabledManually, setVerificationDisabledManually] = useState(false);
  const { MinBet, MaxBet, IsPlayerVerificationEnabled } = useSelector((state) => state.terminal.startupSettings);
  const verification = useSelector((state) => state.terminal.verification);
  const memberId = useSelector((state) => state.terminal.loyalCard.result) || verification.info?.clientId;

  const { verification: userVerification } = useSelector((state) => state.user);
  const userId = useSelector(GetUserId);

  const possWin = useMemo(
    () => calculateBetBuilderPossibleWin(+stake, betData.price, currencyConfig, partner),
    [stake, betData, currencyConfig],
  );

  const { taxedStake } = useMemo(
    () => getBetCalculations([BetCalculationsEnum.TaxOnStake, BetCalculationsEnum.PossibleWin], {
      stake: +stake,
      partnerConfig: partner,
    }),
    [stake, partner],
  );

  const taxAmount = useMemo(
    () => stake && Config.main.fieldsToHide.enableTaxOnReturnField
      && Helpers.calculateTaxForReturnedAndProfit(
        Config,
        partner?.tax_percent,
        partner?.tax_type === 2 ? possWin : possWin - stake,
        partner?.tax_amount_ranges,
      ),
    [stake],
  );

  useEffect(() => {
    setBalanceError((!!stake && +stake > terminalBalance) ? t('Insufficient balance') : '')
  }, [terminalBalance, stake])

  const isStakeValid = +stake >= MinBet && +stake <= (MaxBet || Number.MAX_SAFE_INTEGER);
  const isVerificationDone = !IsPlayerVerificationEnabled || verification.isVerified;
  const placeBetDisabled =
    placeBetLoading || !+stake || +stake > terminalBalance || !isStakeValid || !isVerificationDone;

  const placeBet = async () => {
    setPlaceBetLoading(true);

    if (userVerification?.success) {
      const validationResult = await verifyUserAmount(userVerification.data, +stake);

      if (!validationResult?.success) {
        setPlaceBetLoading(false);
        return (
          validationResult?.errorMessage &&
          ToastWrapper("error", validationResult.errorMessage)
        );
      }
    }

    const placeBetResponse = await betBuilderPlaceBet(betData.betslipUid, stake, userId, userVerification?.data?.id || memberId);

    if (!placeBetResponse) return;

    if (placeBetResponse.data?.Id) {
      const ticketResult = await swarmRequest(
        {
          bar_code: placeBetResponse.data.Id * 10,
          is_with_cashout: true,
          token,
        },
        'check_terminal_bet_ticket',
      );

      const ticket = {
        ...(ticketResult?.data || {}),
        price: betData.formattedPrice,
      };

      if (userVerification?.success) {
        ticketResult?.data && createUserBet(userVerification.data, ticketResult.data);

        ticket.player_name = userVerification.data.name || '-';
        terminalBalance - placeBetResponse.data.Amount <= 0 && logoutUser();
      }

      setStake(0);

      ticketResult?.data && dispatch(PrintBetBuilderTicket(JSON.stringify(ticket)));

      setPlaceBetError(null);
      closePopupByType(POPUPS.BET_BUILDER);
    } else {
      setPlaceBetError(placeBetResponse.data);
    }

    setPlaceBetLoading(false);
  };

  const resolveVerification = async () => {
    const result = await applyUser();
    result?.success && result.data.skipped && setVerificationDisabledManually(true);
  };

  const totalWin = possWin - taxAmount;

  return (
    <div className="bet-builder-betslip">
      <div className="selections">
        <div className="selections-header">
          <div className="selection-header-left">
            <div className="selection-header-right-holder">
              <p className="selections-clear-all-txt title">{t('Betslip')}</p>
              <div className="count-badge-block">{betData.selections.length}</div>
            </div>
          </div>
          <div className="selection-header-right">
            <div className="selection-header-right-holder">
              <button type="button" className="selections-clear-all" onClick={onClear}>
                <i className="global-icon icon-bin" />
                <p className="selections-clear-all-txt">{t('Clear All')}</p>
              </button>
            </div>
          </div>
        </div>
        <div className="section-wrap">
          {betData.selections.map((item) => (
            <div key={item.title} className="selection">
              <div className="sel-holder">
                <p className="match-result-txt">{item.marketName}</p>
                <div className="match-team-name-block">
                  <p className="match-team-name">{item.base}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bet-info">
        <div className="bet-info-top">
          <NumericInput
            onChange={setStake}
            value={stake}
            placeholder={t('Stake')}
            maxDecimalCount={currencyConfig?.rounding || 2}
          />

          <StaticStake setStake={setStake} activeStake={+stake} user={user} terminalBalance={terminalBalance} />
        </div>
        {!!stake && !isStakeValid && (
          <div className="min-max-amount">
            <i className="global-icon icon-filled-info" />
            {` Min Bet Amount ${MinBet} ${MaxBet ? `- Max Bet Amount ${MaxBet}` : ''} `}
          </div>
        )}
        {!isVerificationDone && (
          <div className="min-max-amount">
            {t("Verify yourself using your ID card or go to the cashier for assistance")}
          </div>
        )}

        {Config.verification?.enabled && !Config.verification?.required && !verification.isVerified && !userVerification?.success && (
          <VerificationToggle
            value={verificationDisabledManually}
            onChange={setVerificationDisabledManually}
          />
        )}

        <div className="bet-info-bottom">
          {placeBetError && <div className="bet-info-text-block error">{placeBetError}</div>}
          {Config.main.fieldsToHide.enableError && balanceError && <div className="bet-info-text-block error">{balanceError}</div>}
          <div className="bet-info-text-block">
            {t("Total Odd:")} <span className="bet-info-text-green">{betData.formattedPrice}</span>
          </div>
          {Config.betBuilder.enableTaxOnStake && Config.main.fieldsToHide.enableTaxOnStakeField && !!taxedStake && (
            <div className="bet-info-text-block">
              {t("Tax on stake")}: {Config.betslip.taxPercentOnStake ? `${Config.betslip.taxPercentOnStake}%` : ""}
              <span className="bet-info-text-green">
                <MoneyAmount config={Config} currencyConfig={currencyConfig} amount={+stake - taxedStake} />
              </span>
            </div>
          )}
          {!!taxedStake && (
            <div className="bet-info-text-block">
              {t('Total stake:')}
              <span className="bet-info-text-green">
                <MoneyAmount config={Config} currencyConfig={currencyConfig} amount={taxedStake} />
              </span>
            </div>
          )}
          {!!possWin && (
            <div className="bet-info-text-block">
              {t('Possible win:')}
              <span className="bet-info-text-green">
                <MoneyAmount config={Config} currencyConfig={currencyConfig} amount={possWin} />
              </span>
            </div>
          )}
          {Config.main.fieldsToHide.enableTaxOnReturnField && !!taxAmount && (
            <div className="bet-info-text-block">
              {t('Tax on Return:')}
              <span className="bet-info-text-green">
                <MoneyAmount config={Config} currencyConfig={currencyConfig} amount={taxAmount} />
              </span>
            </div>
          )}
          {Config.main.fieldsToHide.enableTotalWinField && !!totalWin && (
            <div className="bet-info-text-block">
              {t('Total win:')}
              <span className="bet-info-text-green">
                <MoneyAmount config={Config} currencyConfig={currencyConfig} amount={totalWin} />
              </span>
            </div>
          )}

          <VerificationAccount />

          {Config.verification?.enabled && !userVerification?.success && !verificationDisabledManually ? (
            <button className="bet-info-verify-button" onClick={resolveVerification}>
              {t(Config.verification.placeBetText || 'Place bet')}
            </button>
          ) : (
            (Config.main.fieldsToHide.enablePlaceBet && <button disabled={placeBetDisabled} className="bet-info-place-bet-button" onClick={placeBet}>
              {t("Place bet")}
            </button>)
          )}
        </div>
      </div>
    </div>
  );
};

BetBuliderBetslip.propTypes = {
  betData: PropTypes.object.isRequired,
  onClear: PropTypes.func.isRequired,
  user: PropTypes.object,
  terminalBalance: PropTypes.number,
  currencyConfig: PropTypes.object,
  token: PropTypes.string,
  partner: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
};

const mapStateToProps = () =>
  createSelector(
    [
      GetUser,
      (state) => GetCurrencyConfig(state, Config.main.currencyRounding),
      GetToken,
      (state) => state.swarmConfigData.partner,
    ],
    (user, currencyConfig, token, partner) => ({
      user,
      currencyConfig,
      token,
      partner,
    })
  );

export default connect(mapStateToProps)(BetBuliderBetslip);
